import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FundClosingStatusEnumType } from '__generated__/graphql';
import dayjs from 'dayjs';
import { FC, useEffect } from 'react';

import { FormInput, FormPanelWithReadMode } from 'components/FormPanel';

const UPDATE_FUND_CLOSING_MUTATION = gql(`
  mutation UpdateFundClosingStatus(
    $id: ID!
    $status: FundClosingStatusEnumType!
    $wiredAt: String
    $executedAt: String
    $finalisedAt: String
  ) {
    updateFundClosingStatus(
      id: $id
      status: $status
      wiredAt: $wiredAt
      executedAt: $executedAt
      finalisedAt: $finalisedAt
    ) {
      id
      status
      wiredAt
      executedAt
      finalisedAt
      updatedAt
      fund {
        id
        statistics {
          totalRaisedAmount
        }
      }
    }
  }
`);

const UpdateFundClosingStatus: FC<{
  id: string;
  status: FundClosingStatusEnumType;
  wiredAt?: string | null;
  executedAt?: string | null;
  finalisedAt?: string | null;
}> = ({ id, status, wiredAt, executedAt, finalisedAt }) => {
  const [updateFundClosing, { loading, error }] = useMutation(UPDATE_FUND_CLOSING_MUTATION, {
    refetchQueries: ['getFundClosingAudit', 'getFundClosingEmailAudit'],
  });

  useEffect(() => {
    console.log('wiredAt prop:', wiredAt);
    console.log('status prop:', status);
  }, [wiredAt, status]);

  return (
    <FormPanelWithReadMode
      loading={loading}
      error={error}
      onSubmit={data => {
        console.log('Form data:', data);
        console.log('wiredAt in form:', data.wiredAt);
        console.log('status in form:', data.status);
        updateFundClosing({
          variables: {
            id,
            status: data.status,
            wiredAt: data.wiredAt,
            executedAt: data.executedAt,
            finalisedAt: data.finalisedAt,
          },
        });
      }}
    >
      <FormInput
        type="select"
        fieldName="status"
        label="Status"
        defaultValue={status}
        options={[
          { value: 'CREATED', label: 'Created', color: 'bg-blue-500' },
          { value: 'COMMITTED', label: 'Committed', color: 'bg-yellow-500' },
          { value: 'TRANSFERRED', label: 'Transferred', color: 'bg-purple-500' },
          { value: 'EXECUTED', label: 'Executed', color: 'bg-indigo-500' },
          { value: 'ACTIVE', label: 'Active', color: 'bg-pink-500' },
          { value: 'EXITED', label: 'Exited', color: 'bg-green-500' },
          { value: 'CANCELLED', label: 'Cancelled', color: 'bg-red-500' },
        ]}
      />
      <FormInput
        type="date"
        fieldName="wiredAt"
        label="Wired At"
        defaultValue={wiredAt ? dayjs(wiredAt).format('YYYY-MM-DD') : ''}
        conditionsToShow={{
          matches: [
            {
              field: 'status',
              condition: '===',
              value: 'TRANSFERRED',
            },
            {
              field: 'status',
              condition: '===',
              value: 'EXECUTED',
            },
          ],
          type: 'some',
        }}
      />
      <FormInput
        type="date"
        fieldName="executedAt"
        label="Executed At"
        defaultValue={executedAt || ''}
        conditionsToShow={{
          matches: [
            {
              field: 'status',
              condition: '===',
              value: 'EXECUTED',
            },
            {
              field: 'status',
              condition: '===',
              value: 'FINALISED',
            },
          ],
          type: 'some',
        }}
      />
      <FormInput
        type="date"
        fieldName="finalisedAt"
        label="Finalised At"
        defaultValue={finalisedAt || ''}
        conditionsToShow={{
          matches: [
            {
              field: 'status',
              condition: '===',
              value: 'ACTIVE',
            },
          ],
        }}
      />
    </FormPanelWithReadMode>
  );
};

export default UpdateFundClosingStatus;
